import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Phone, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { SITE, waLink } from "@/data/site";
import { WhatsAppIcon } from "./Icons";

const DISMISS_KEY = "hammer_wa_dismissed";
const DELAY_MS = 3000;

export const WhatsAppPopup = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (dismissed) return;
    const id = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(id);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="whatsapp-popup"
            role="dialog"
            aria-label="WhatsApp"
            initial={{ opacity: 0, y: 40, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed z-[70] right-3 left-3 sm:left-auto bottom-[88px] md:bottom-28 md:right-6 sm:w-[360px] bg-bone text-ink border-[3px] border-ink shadow-[8px_8px_0_#0F0F10]"
          >
            <div className="flex items-center gap-3 bg-ink text-bone px-4 py-3">
              <span className="relative w-11 h-11 bg-hammer-red border-2 border-bone grid place-items-center font-display text-2xl pt-1 shrink-0">
                H
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-whatsapp border-2 border-ink" />
              </span>
              <div className="leading-tight flex-1 min-w-0">
                <p className="font-display text-2xl leading-none pt-1">Hammer Gym Lugano</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-bone/60 mt-1 truncate">{t.wa.status}</p>
              </div>
              <button
                type="button"
                onClick={close}
                data-testid="whatsapp-close-button"
                aria-label={t.wa.close}
                className="w-9 h-9 grid place-items-center border-2 border-bone/40 hover:border-bone hover:bg-hammer-red transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              <div className="relative bg-white border-2 border-ink px-4 py-3 text-[15px] leading-snug">
                {t.wa.msg}
                <span className="block text-right font-mono text-[10px] text-ink/40 mt-1">Hammer Gym · {SITE.phoneDisplay}</span>
              </div>
              <div className="mt-4 grid grid-cols-[1fr_auto] gap-3">
                <a
                  href={waLink(t.wa.prefill)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-open-button"
                  onClick={close}
                  className="btn-brutal btn-wa !text-lg !py-3"
                >
                  <WhatsAppIcon className="w-5 h-5" /> {t.wa.cta}
                </a>
                <a href={SITE.tel} data-testid="whatsapp-call-button" aria-label="Call" className="btn-brutal btn-ink !px-4 !py-3">
                  <Phone className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* floating button (desktop / tablet) */}
      <a
        href={waLink(t.wa.prefill)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-fab"
        aria-label="WhatsApp"
        className="hidden md:grid fixed z-[60] bottom-6 right-6 w-16 h-16 place-items-center rounded-full bg-whatsapp text-ink border-[3px] border-ink shadow-[5px_5px_0_#0F0F10] hover:shadow-[7px_7px_0_#0F0F10] hover:-translate-y-0.5 transition-[box-shadow,translate]"
      >
        <WhatsAppIcon className="w-8 h-8" />
      </a>
    </>
  );
};

export const MobileBar = () => {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > window.innerHeight * 0.6);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <div
      data-testid="mobile-sticky-bar"
      className={`md:hidden fixed bottom-0 inset-x-0 z-[65] grid grid-cols-3 bg-ink border-t-[3px] border-hammer-red transition-transform duration-300 ${
        show ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <a href={SITE.tel} data-testid="mobile-bar-call" className="flex flex-col items-center justify-center py-2.5 text-bone gap-1 border-r border-bone/15">
        <Phone className="w-5 h-5" />
        <span className="font-mono text-[10px] uppercase tracking-widest">{t.mobile.call}</span>
      </a>
      <a href={waLink(t.wa.prefill)} target="_blank" rel="noopener noreferrer" data-testid="mobile-bar-whatsapp" className="flex flex-col items-center justify-center py-2.5 text-whatsapp gap-1 border-r border-bone/15">
        <WhatsAppIcon className="w-5 h-5" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-bone">{t.mobile.wa}</span>
      </a>
      <a href="#prova" data-testid="mobile-bar-trial" className="flex flex-col items-center justify-center py-2.5 bg-hammer-red text-bone gap-1">
        <ArrowUpRight className="w-5 h-5" />
        <span className="font-mono text-[10px] uppercase tracking-widest">{t.mobile.trial}</span>
      </a>
    </div>
  );
};
